import React, {
    useEffect,
    useState,
    useCallback
} from 'react';

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Stack,
    CircularProgress
} from '@mui/material';

import {
    getPrintingProcess,
    updatePrintingProcess
} from '../../services/PrintingProcessService';

const PrintingProcessUpdate = ({
    open,
    id,
    onClose,
    onSuccess
}) => {

    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        printed_quantity: '',
        rejected_quantity: ''
    });

    const [errors, setErrors] = useState({});

    /**
     * Load selected record
     */
    const loadRecord = useCallback(async () => {

        if (!id) {
            return;
        }

        try {

            setLoading(true);
            setErrors({});

            const response =
                await getPrintingProcess(id);

            console.log(
                'Printing Process Response:',
                response.data
            );

            const record =
                response.data?.data;

            if (!record) {
                console.error(
                    'Printing Process record not found.'
                );

                return;
            }

            setFormData({
                printed_quantity:
                    record.printed_quantity ?? '',

                rejected_quantity:
                    record.rejected_quantity ?? ''
            });

        } catch (error) {

            console.error(
                'Get Printing Process Error:',
                error
            );

            setErrors({
                general:
                    error.response?.data?.message ||
                    'Failed to load Printing Process.'
            });

        } finally {

            setLoading(false);
        }

    }, [id]);

    /**
     * Load record whenever dialog opens
     * or selected ID changes
     */
    useEffect(() => {

        if (open && id) {
            loadRecord();
        }

    }, [open, id, loadRecord]);

    /**
     * Handle input change
     */
    const handleChange = (event) => {

        const {
            name,
            value
        } = event.target;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        setErrors(prev => ({
            ...prev,
            [name]: ''
        }));
    };

    /**
     * Validate form
     */
    const validate = () => {

        const newErrors = {};

        if (
            formData.printed_quantity === '' ||
            Number(formData.printed_quantity) < 0
        ) {
            newErrors.printed_quantity =
                'Printed quantity is required.';
        }

        if (
            formData.rejected_quantity !== '' &&
            Number(formData.rejected_quantity) < 0
        ) {
            newErrors.rejected_quantity =
                'Rejected quantity cannot be negative.';
        }

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    /**
     * Update record
     */
    const handleSubmit = async () => {

        if (!validate()) {
            return;
        }

        try {

            setLoading(true);

            const payload = {
                printed_quantity:
                    Number(formData.printed_quantity),

                rejected_quantity:
                    formData.rejected_quantity === ''
                        ? 0
                        : Number(formData.rejected_quantity)
            };

            console.log(
                'Update Printing Process Payload:',
                payload
            );

            await updatePrintingProcess(
                id,
                payload
            );

            onSuccess();

        } catch (error) {

            console.error(
                'Update Printing Process Error:',
                error
            );

            if (error.response?.status === 422) {

                setErrors(
                    error.response.data.errors || {}
                );

            } else {

                setErrors({
                    general:
                        error.response?.data?.message ||
                        'Failed to update Printing Process.'
                });
            }

        } finally {

            setLoading(false);
        }
    };

    /**
     * Close dialog
     */
    const handleClose = () => {

        if (loading) {
            return;
        }

        setErrors({});

        setFormData({
            printed_quantity: '',
            rejected_quantity: ''
        });

        onClose();
    };

    return (

        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
        >

            <DialogTitle>
                Update Printing Process
            </DialogTitle>

            <DialogContent>

                <Stack
                    spacing={2}
                    sx={{ mt: 1 }}
                >

                    {errors.general && (
                        <TextField
                            value={errors.general}
                            error
                            fullWidth
                            multiline
                            InputProps={{
                                readOnly: true
                            }}
                        />
                    )}

                    <TextField
                        label="Printed Quantity"
                        name="printed_quantity"
                        type="number"
                        fullWidth
                        required
                        value={
                            formData.printed_quantity
                        }
                        onChange={handleChange}
                        error={
                            Boolean(
                                errors.printed_quantity
                            )
                        }
                        helperText={
                            errors.printed_quantity
                        }
                        inputProps={{
                            min: 0
                        }}
                        disabled={loading}
                    />

                    <TextField
                        label="Rejected Quantity"
                        name="rejected_quantity"
                        type="number"
                        fullWidth
                        value={
                            formData.rejected_quantity
                        }
                        onChange={handleChange}
                        error={
                            Boolean(
                                errors.rejected_quantity
                            )
                        }
                        helperText={
                            errors.rejected_quantity
                        }
                        inputProps={{
                            min: 0
                        }}
                        disabled={loading}
                    />

                </Stack>

            </DialogContent>

            <DialogActions>

                <Button
                    onClick={handleClose}
                    disabled={loading}
                >
                    Cancel
                </Button>

                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || !id}
                >

                    {loading ? (
                        <CircularProgress size={22} />
                    ) : (
                        'Update'
                    )}

                </Button>

            </DialogActions>

        </Dialog>
    );
};

export default PrintingProcessUpdate;